import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, GripVertical, Image as ImageIcon } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", logo: "", website: "" };

export default function ClientsManager() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState({}); // id -> local edits
  const [saving, setSaving] = useState(null); // id currently saving
  const [creating, setCreating] = useState(EMPTY);
  const [uploadingId, setUploadingId] = useState(null); // 'new' or client id
  const dragIndex = useRef(null);
  const fileInputs = useRef({});

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data || []);
  };
  useEffect(() => { load(); }, []);

  const setField = (id, key, value) => {
    setEditing((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: value } }));
  };

  const getVal = (c, key) => (editing[c.id]?.[key] !== undefined ? editing[c.id][key] : c[key] || "");

  const uploadLogo = async (e, forId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(forId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (forId === "new") setCreating((c) => ({ ...c, logo: data.url }));
      else setField(forId, "logo", data.url);
      toast.success("Logo uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploadingId(null);
      if (fileInputs.current[forId]) fileInputs.current[forId].value = "";
    }
  };

  const saveOne = async (c) => {
    setSaving(c.id);
    try {
      const merged = {
        name: getVal(c, "name"),
        logo: getVal(c, "logo"),
        website: getVal(c, "website"),
      };
      await api.put(`/clients/${c.id}`, merged);
      toast.success("Saved.");
      setEditing((e) => { const n = { ...e }; delete n[c.id]; return n; });
      load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(null);
    }
  };

  const removeClient = async (id) => {
    if (!confirm("Remove this client slot?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const addClient = async () => {
    if (!creating.name.trim() && !creating.logo) {
      toast.error("Add a name or upload a logo first.");
      return;
    }
    try {
      await api.post("/clients", creating);
      toast.success("Client added.");
      setCreating(EMPTY);
      load();
    } catch {
      toast.error("Failed to add client.");
    }
  };

  // Drag & drop reorder
  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setClients((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
  };
  const onDragEnd = async () => {
    dragIndex.current = null;
    try {
      await api.put("/clients/reorder", { ids: clients.map((c) => c.id) });
      toast.success("Order saved.");
    } catch {
      toast.error("Failed to save order.");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-clients-manager">
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8] mb-1">Home page</p>
        <h2 className="font-display font-semibold text-xl text-[#334155] mb-1">Client logos (Our Clients marquee)</h2>
        <p className="text-sm text-[#52525B]">Drag rows to reorder. Empty slots render as placeholders on the site until a logo is uploaded.</p>
      </div>

      {/* Add new */}
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#78be27] mb-4">Add a new client</p>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Name</Label>
            <Input
              value={creating.name}
              onChange={(e) => setCreating({ ...creating, name: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="e.g. Tata Motors"
              data-testid="admin-client-new-name"
            />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Website (optional)</Label>
            <Input
              value={creating.website}
              onChange={(e) => setCreating({ ...creating, website: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="https://…"
              data-testid="admin-client-new-website"
            />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Logo</Label>
            <div className="mt-1 flex items-center gap-2">
              <div className="w-12 h-12 border border-black/10 bg-[#F9FAFB] flex items-center justify-center shrink-0">
                {creating.logo ? (
                  <img src={resolveImg(creating.logo)} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon size={16} className="text-black/25" />
                )}
              </div>
              <label className="inline-flex items-center gap-2 border border-black/15 px-3 py-2 cursor-pointer hover:border-[#3da8d8] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
                <Upload size={12} /> {uploadingId === "new" ? "Uploading…" : "Upload"}
                <input
                  ref={(el) => { fileInputs.current["new"] = el; }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadLogo(e, "new")}
                  data-testid="admin-client-new-logo-file"
                />
              </label>
            </div>
          </div>
          <button
            type="button"
            onClick={addClient}
            data-testid="admin-client-add-btn"
            className="bg-[#3da8d8] text-white h-11 flex items-center justify-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
          >
            <Plus size={14} /> Add Client
          </button>
        </div>
      </div>

      {/* Existing */}
      <div className="border border-black/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8]">
            {clients.length} slot{clients.length !== 1 ? "s" : ""} — drag to reorder
          </p>
        </div>
        <div className="space-y-3">
          {clients.map((c, i) => {
            const dirty = !!editing[c.id];
            return (
              <div
                key={c.id}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragEnter={() => onDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={onDragEnd}
                data-testid={`admin-client-row-${c.id}`}
                className={`grid grid-cols-[auto_auto_1fr_1.5fr_auto_auto] items-center gap-3 border p-3 bg-white ${dirty ? "border-[#78be27]" : "border-black/10"}`}
              >
                <GripVertical size={16} className="text-[#94A3B8] cursor-grab active:cursor-grabbing" />
                <div className="w-14 h-14 border border-black/10 bg-[#F9FAFB] flex items-center justify-center overflow-hidden shrink-0">
                  {getVal(c, "logo") ? (
                    <img src={resolveImg(getVal(c, "logo"))} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon size={16} className="text-black/25" />
                  )}
                </div>
                <Input
                  value={getVal(c, "name")}
                  onChange={(e) => setField(c.id, "name", e.target.value)}
                  className="rounded-none border-black/15"
                  placeholder="Client name"
                  data-testid={`admin-client-name-${c.id}`}
                />
                <Input
                  value={getVal(c, "website")}
                  onChange={(e) => setField(c.id, "website", e.target.value)}
                  className="rounded-none border-black/15"
                  placeholder="https://…"
                  data-testid={`admin-client-website-${c.id}`}
                />
                <label className="inline-flex items-center gap-2 border border-black/15 px-3 py-2 cursor-pointer hover:border-[#3da8d8] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
                  <Upload size={12} /> {uploadingId === c.id ? "…" : "Logo"}
                  <input
                    ref={(el) => { fileInputs.current[c.id] = el; }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadLogo(e, c.id)}
                    data-testid={`admin-client-logo-file-${c.id}`}
                  />
                </label>
                <div className="flex items-center gap-1">
                  {dirty && (
                    <button
                      type="button"
                      onClick={() => saveOne(c)}
                      disabled={saving === c.id}
                      data-testid={`admin-client-save-${c.id}`}
                      className="w-9 h-9 flex items-center justify-center bg-[#78be27] text-white hover:bg-[#6aa81f] disabled:opacity-50"
                    >
                      <Save size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeClient(c.id)}
                    data-testid={`admin-client-delete-${c.id}`}
                    className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#52525B] hover:text-red-600 hover:border-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {clients.length === 0 && (
            <p className="text-sm text-[#94A3B8] italic">No clients yet — add one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}
